import { EErrorCode } from 'config/errors-codes';
import type { IBrand, IMarket } from 'data/schema';
import { parseDealerId } from './dealerIdFormat';
import type { IDealerIdOption, IUnavailableDealerId, TDealerUnavailableReason } from './dealerResolution.types';

interface IDealerIdOptionsContext {
  brand: IBrand;
  market: IMarket;
}

interface IOrderDealerIdOptionsOptions {
  unavailable?: readonly IUnavailableDealerId[];
  linkedDealerId?: string;
}

/**
 * Turns the account's Dealer IDs into the window's flat option list (no grouping).
 * The IDs that fit the given brand and market come first, then the rest, each part keeping account
 * (login-claims) order. A fitting linked ID moves to the very front and is flagged `isFromLink`; a
 * linked ID that does not fit stays in place, unflagged. Ruled-out IDs keep `unavailableReason`, a
 * linked one included. IDs that do not parse are skipped.
 */
export function orderDealerIdOptions(
  dealerIds: readonly string[],
  { brand, market }: IDealerIdOptionsContext,
  { unavailable, linkedDealerId }: IOrderDealerIdOptionsOptions = {}
): IDealerIdOption[] {
  const unavailableReasonByDealerId = new Map<string, TDealerUnavailableReason>(
    (unavailable ?? []).map(({ dealerId, code }) => [dealerId, getUnavailableReason(code)])
  );

  let linkedOption: IDealerIdOption | undefined;
  const fitting: IDealerIdOption[] = [];
  const rest: IDealerIdOption[] = [];

  for (const dealerId of dealerIds) {
    const parsed = parseDealerId(dealerId);
    if (!parsed) continue;

    const fitsContext = parsed.brand === brand && parsed.market === market;
    const unavailableReason = unavailableReasonByDealerId.get(dealerId);
    const option: IDealerIdOption = {
      dealerId,
      fitsContext,
      ...(unavailableReason ? { unavailableReason } : {}),
    };

    if (fitsContext && !linkedOption && dealerId === linkedDealerId) {
      linkedOption = { ...option, isFromLink: true };
    } else {
      (fitsContext ? fitting : rest).push(option);
    }
  }

  return linkedOption ? [linkedOption, ...fitting, ...rest] : [...fitting, ...rest];
}

/** A trimmed, case-insensitive substring match on the Dealer ID text only; keeps the order. */
export function filterDealerIdOptions(options: readonly IDealerIdOption[], query: string): IDealerIdOption[] {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) {
    return [...options];
  }

  return options.filter((option) => option.dealerId.toLowerCase().includes(trimmedQuery));
}

/** True when at least one option has no `unavailableReason`. */
export function hasAvailableDealerId(options: readonly IDealerIdOption[]): boolean {
  return options.some((option) => !option.unavailableReason);
}

/** True when a listed option has this Dealer ID and no `unavailableReason`; false for an unlisted or undefined ID. */
export function isOptionAvailable(options: readonly IDealerIdOption[], dealerId?: string): boolean {
  return dealerId !== undefined && options.some((option) => option.dealerId === dealerId && !option.unavailableReason);
}

/**
 * The account's only Dealer ID when it fits the given brand and market, else `undefined`.
 * Mode-agnostic (list plus brand/market in, ID or undefined out): no storage, user or window
 * knowledge, so it is reusable by AVS-4407/4408.
 */
export function getSingleFittingDealerId(
  dealerIds: readonly string[],
  { brand, market }: IDealerIdOptionsContext
): string | undefined {
  if (dealerIds.length !== 1) {
    return undefined;
  }

  const [onlyDealerId] = dealerIds;
  const parsed = parseDealerId(onlyDealerId);

  return parsed && parsed.brand === brand && parsed.market === market ? onlyDealerId : undefined;
}

/** DISABLED_MARKET gives `'market-disabled'`; any other code gives `'not-authorized'`. */
export function getUnavailableReason(code: EErrorCode): TDealerUnavailableReason {
  return code === EErrorCode.DISABLED_MARKET ? 'market-disabled' : 'not-authorized';
}

/**
 * Which Dealer IDs a step 2/3 failure rules out for the rest of the visit: for DISABLED_MARKET,
 * every account Dealer ID of the failed ID's brand and market (plus the failed ID itself); for any
 * other code, only the failed Dealer ID.
 */
export function getDealerIdsToRuleOut(
  dealerIds: readonly string[],
  failedDealerId: string,
  code: EErrorCode
): string[] {
  if (code !== EErrorCode.DISABLED_MARKET) {
    return [failedDealerId];
  }

  const failedParsed = parseDealerId(failedDealerId);
  if (!failedParsed) {
    return [failedDealerId];
  }

  return dealerIds.filter((dealerId) => {
    const parsed = parseDealerId(dealerId);
    return parsed && parsed.brand === failedParsed.brand && parsed.market === failedParsed.market;
  });
}
